/**
 * #925: whether a pasted private key is encrypted, read from its PEM header lines only, in the
 * browser. Nothing here logs, stores or returns any part of the key: the answer is one word.
 *
 * - A PKCS#8 "BEGIN ENCRYPTED PRIVATE KEY" header needs a passphrase.
 * - A PKCS#8 "BEGIN PRIVATE KEY" header needs none; nor does a traditional RSA, EC or DSA one,
 *   unless its `Proc-Type: 4,ENCRYPTED` header says it is.
 * - Anything else is `unknown`, and the form says nothing about the passphrase.
 *
 * The header patterns are assembled from their parts so this file holds no literal PEM armour
 * line, which secret scanners (gitleaks `private-key`) read as a key.
 */
export type PemKeyKind = "encrypted" | "unencrypted" | "unknown";

const DASHES = "-{5}";
const ENCRYPTED_PKCS8 = new RegExp(`${DASHES}BEGIN ENCRYPTED PRIVATE KEY${DASHES}`);
const PLAIN_OR_TRADITIONAL = new RegExp(`${DASHES}BEGIN (?:RSA |EC |DSA )?PRIVATE KEY${DASHES}`);
const PROC_TYPE_ENCRYPTED = /^Proc-Type:\s*4,ENCRYPTED\s*$/m;

export function pemKeyKind(value: string): PemKeyKind {
  if (ENCRYPTED_PKCS8.test(value)) return "encrypted";
  if (!PLAIN_OR_TRADITIONAL.test(value)) return "unknown";
  return PROC_TYPE_ENCRYPTED.test(value) ? "encrypted" : "unencrypted";
}

/** The passphrase field's sentence when the key and the passphrase disagree, else null. */
export function passphraseRefusal(kind: PemKeyKind, passphraseSet: boolean): string | null {
  if (kind === "encrypted" && !passphraseSet) {
    return "This private key is encrypted. Set its passphrase too.";
  }
  if (kind === "unencrypted" && passphraseSet) {
    return "This private key is not encrypted, so it takes no passphrase. Clear the passphrase.";
  }
  return null;
}
